<template>
    <dx-data-grid
        ref="dataGridRef"
        :allow-column-reordering="true"
        :column-chooser="{ enabled: true, mode: 'select' }"
        :column-min-width="50"
        :columns="columns"
        :data-source="dataSource"
        :filter-panel="{ visible: true }"
        :filter-row="{ visible: true }"
        :header-filter="{ visible: true }"
        :height="50"
        :hover-state-enabled="true"
        :repaint-changes-only="true"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :width="'100%'"
        :word-wrap-enabled="true"
        style="flex-grow: 1"
        value-expr="id"
        @toolbar-preparing="onToolbarPreparing($event)"
        @on-cell-prepared="onCellPrepared($event)"
        @content-ready="onContentReady($event)"
        @cell-dbl-click="onInventoryNumberClick($event)"
    >
        <dx-paging :enabled="true" :page-size="40" />
        <dx-load-panel :enabled="true" />
        <dx-scrolling mode="virtual" />
        <dx-sorting mode="single" />
        <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
        <dx-editing
            :allow-deleting="isEditable"
            :allow-updating="isEditable"
            :refresh-mode="'full'"
            mode="batch"
        />
        <template #chooseDepartment="{ data: rowData }">
            <DxDropDownBox
                v-model:value="rowData.data.department"
                :display-expr="() => showDepartment(rowData)"
                :show-clear-button="false"
            >
                <template #content>
                    <DxTreeList
                        :columns="gridColumns"
                        :data-source="lookupData"
                        :filter-row="{ visible: true }"
                        :focused-row-enabled="true"
                        :focused-row-key="rowData.data.department"
                        key-expr="kod_direct"
                        style="max-height: 400px; overflow-y: auto;"
                        @focused-row-changed="(e) => onFocusedRowChanged(e, rowData)"
                    />
                </template>
            </DxDropDownBox>
        </template>
        <template #chooseGroup="{ data: rowData, value }">
            <DxSelectBox
                :data-source="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]"
                :value="value"
                placeholder="Выберите группу"
                @value-changed="e => rowData.setValue(e.value)"
            />
        </template>

        <template #filterTemplatePeriod>
            <mped-calculation-period-component
                :periodFnSource="getUploadPeriods"
                :placeholder="'Базовый период'"
                :selectionMode="'single'"
                :show-clear-button="false"
                :validate-required="true"
                :validation-status="validationStatus"
                @value-changed="onPeriodChanged($event)"
            />
        </template>
    </dx-data-grid>
    <footer-grid :show-selected-counter="false" :totalCount="totalCount" class="px-2"></footer-grid>
    <mped-popup-grid-view
        ref="mainAssetChooser"
        :choose-item="true"
        :columns="columnsAmortizationMainAsset"
        :fn-get-data="getMainAsset"
        popupTitle="Справочник основных средств"
        @selected-item="selectedMainAsset($event)"
    />
    <mped-popup-grid-view
        ref="periodChooser"
        :choose-item="true"
        :columns="columnsPeriods"
        :fn-get-data="getBasePeriod"
        popupTitle="Периоды"
        @selected-item="selectedPeriod"
    />
</template>

<script>
export default {
    name: 'mped-amortization-plan-work-group-view',
};
</script>

<script setup>
import { onBeforeMount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
    DxDataGrid,
    DxEditing,
    DxExport,
    DxLoadPanel,
    DxPaging,
    DxScrolling,
    DxSorting,
} from 'devextreme-vue/data-grid';
import { DxTreeList } from 'devextreme-vue/tree-list';
import { DxDropDownBox } from 'devextreme-vue/drop-down-box';
import { DxSelectBox } from 'devextreme-vue';

import { addNewRowData, createFilterArray, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { columnsAmortizationPlanWork, mvpLookupColumns } from '@/views/mped/services/mped-amortization-service';
import {
    getDistributionLookup,
    getMainAsset,
    getPlanWorkGroup, getUploadPeriods,
    setPlanWorkGroup,
} from '@/views/mped/services/mped-amortization-api-service';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';
import MpedPopupGridView from '@/views/mped/components/mped-popup-grid-view/mped-popup-grid-view.vue';
import { columnsAmortizationMainAsset } from '@/views/mped/services/mped-calculation-service';
import MpedCalculationPeriodComponent
    from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import { hasEditablePermissionByUIRoute } from '@/service/local-userdata-service';
import FooterGrid from '@/components/grid-card-component/footer-grid.vue';

const dataGridRef = ref();
const mainAssetChooser = ref();
const periodChooser = ref();
const columns = columnsAmortizationPlanWork('chooseDepartment', 'chooseGroup');
const currentMainAsset = ref();
const currentPeriod = ref();
const gridColumns = mvpLookupColumns;
const periodsValue = ref(null);
const validationStatus = ref(null);
const router = useRouter();
const isEditable = hasEditablePermissionByUIRoute(router);
const totalCount = ref(0);
let copyData = ref();
let lookupData = [];
let toolbarButtons = {};
let copyButton;

const columnsPeriods = [{
    dataField: 'id',
    caption: 'Период',
    dataType: 'string',
    width: '100px',
}];

onBeforeMount(async () => {
    const response = await getDistributionLookup();
    lookupData = response.data;
});

watch(periodsValue, (newValue) => {
    validationStatus.value = newValue ? 'valid' : 'invalid';
}, { immediate: true });

const dataSource = getAiDataSource(
    {
        dataFn: getPlanWorkGroup,
        key: 'id',
        remoteOperation: true,
        validationFn: () => {
            return periodsValue.value;
        },
        customFilterFn: () => {
            if (!periodsValue.value.length) return null;

            const periodFilter = [
                periodsValue.value.reduce((total, item) => {
                    total.push(['base_period', '=', item]);
                    return total;
                }, []),
            ];

            return createFilterArray([periodFilter]);
        },
        insertFn: (values) => {
            return setPlanWorkGroup({ insertDataArgs: [values] });
        },
        updateFn: (id, values) => {
            return setPlanWorkGroup({ updateDataArgs: [{ id, ...values }] });
        },
        removeFn: (id) => {
            return setPlanWorkGroup({ deleteDataArgs: [{ id }] });
        },
        onLoadedFn: (data) => {
            totalCount.value = data.totalCount;
        },
    },
);

const onFocusedRowChanged = (e, data) => {
    const selected = e.row?.data;
    if (selected && selected.kod_direct !== data.data.department_id) {
        data.setValue(`${selected.mvp_code} - ${selected.name}`);
        data.component.cellValue(data.rowIndex, 'department_id', selected.kod_direct);
        dataGridRef.value.instance.refresh();
    }
};

const onToolbarPreparing = (e) => {
    const filters = [
        {
            location: 'before',
            template: 'filterTemplatePeriod',
        },
        {
            location: 'before',
            locateInMenu: 'auto',
            widget: 'dxButton',
            cssClass: 'toolbar-item-custom-style',
            options: {
                onClick: () => {
                    addPeriod();
                },
                hint: 'Добавить копированием',
                text: 'Добавить копированием',
                disabled: true,
                onInitialized: (e) => {
                    copyButton = e.component;
                },
            },
        },
    ];
    toolbarButtons = getAiToolbar(e, {
        customs: filters,
        refresh: {
            event: reloadData,
        },
        add: {
            event: addData,
        },
        save: {
            event: (e) => {
                dataGridRef.value.instance.saveEditData();
            },
        },
        undo: {
            event: (e) => {
                undoEditData(dataGridRef.value.instance);
                copyData = ref();
            },
        },
    });
};

const addPeriod = () => {
    periodChooser.value.Open();
};

const getBasePeriod = async () => {
    const data = await getUploadPeriods();
    copyData.value = await getPlanWorkGroup();

    if (!periodsValue.value) {
        return data;
    }

    return data.data.filter(item => item.id !== periodsValue.value[0]);
};

const reloadData = async () => {
    await dataSource.reload();
};

const addMainAsset = () => {
    mainAssetChooser.value.Open();
};

const showDepartment = (rowData) => {
    if (rowData?.data?.department) {
        return `${rowData.data.department}`;
    }
};

const selectedMainAsset = (e, reload = true) => {
    currentMainAsset.value = e;
    addData(currentMainAsset.value);

    if (reload) {
        dataSource.reload();
    }
};

const selectedPeriod = (e) => {
    currentPeriod.value = e.id;
    addData(currentPeriod.value);
    reloadData();
};

const onInventoryNumberClick = (e) => {
    if (!e.column || !e.row) return;

    if (e.column.dataField === 'inventory_number') {
        addMainAsset(e);
    }
};

const addData = (e) => {
    const instance = dataGridRef.value.instance;
    instance.cancelEditData();

    const commonFields = [
        'department', 'inventory_number', 'main_asset_id', 'subnumber',
        'group_id', 'work_name', 'date_operation',
        'value', 'department_id', 'base_period',
    ];

    const commonData = {
        dxInstance: instance,
        fields: commonFields,
    };
    if (copyData.value) {
        const filteredItems = copyData.value.filter(item =>
            new Date(item.date_operation) >= new Date(periodsValue?.value[0]) &&
            item.base_period == currentPeriod.value,
        );

        filteredItems.forEach(item => {
            addNewRowData({
                ...commonData,
                sourceData: {
                    ...item,
                    base_period: periodsValue?.value[0],
                },
            });
        });
        copyData.value = null;
    } else {
        addNewRowData({
            ...commonData,
            sourceData: {
                ...e,
                work_name: e.name,
                group_id: e.group_amortization,
                main_asset_id: e.id,
                base_period: periodsValue?.value[0],
            },
        });
    }
};

const onContentReady = (e) => {
    if (!periodsValue.value) {
        toolbarButtons.add.option('disabled', true);
    }
    checkHasEditData(dataGridRef.value.instance, toolbarButtons);
};

const onCellPrepared = (e) => {
    if (e.rowType !== 'data' || e.columnIndex != 0) {
        return;
    }
};

const onPeriodChanged = (e) => {
    periodsValue.value = e;
    toolbarButtons.add.option('disabled', !isEditable);
    copyButton.option('disabled', !isEditable);
    reloadData();
};

</script>
